import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, MONTHS_FR } from "@/lib/api";
import ConcertModal from "@/components/ConcertModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Pencil, Trash2, Download, Music4, Ban, CalendarClock, RotateCcw } from "lucide-react";

export default function PlanningPage() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState({ years: [], months_by_year: {} });
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [concerts, setConcerts] = useState([]);
  const [intermittents, setIntermittents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toCancel, setToCancel] = useState(null);
  const [toPostpone, setToPostpone] = useState(null);
  const [ppForm, setPpForm] = useState({ year: "", month: "", day: "", heure: "" });

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: inter }] = await Promise.all([
          api.get("/periods"),
          api.get("/intermittents"),
        ]);
        setPeriods(p);
        setIntermittents(inter);
        const now = new Date();
        const cy = now.getFullYear();
        const cm = now.getMonth() + 1;
        const y = p.years.includes(cy) ? cy : (p.years[p.years.length - 1] ?? cy);
        const months = p.months_by_year[String(y)] || [];
        const m = months.includes(cm) ? cm : (months[0] ?? cm);
        setYear(y);
        setMonth(m);
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      }
    })();
  }, []);

  const loadConcerts = useCallback(async () => {
    if (year == null || month == null) return;
    setLoading(true);
    try {
      const { data } = await api.get("/concerts", { params: { year, month } });
      setConcerts(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadConcerts(); }, [loadConcerts]);

  const monthsForYear = periods.months_by_year[String(year)] || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return concerts;
    return concerts.filter((c) =>
      [c.concert, c.salle, c.ville, c.prod].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [concerts, search]);

  const norm2 = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const isStruck = (c) => (c.status || "active") !== "active";
  const isMontage = (c) => ["montage", "premontage", "demontage", "prerig"].some((k) => norm2(c.concert).startsWith(k));
  const activeFiltered = useMemo(() => filtered.filter((c) => !isStruck(c) && !isMontage(c)), [filtered]);

  const interTotals = useMemo(() => {
    const t = {};
    intermittents.forEach((i) => (t[i.id] = 0));
    activeFiltered.forEach((c) => (c.intermittent_ids || []).forEach((id) => { if (t[id] != null) t[id]++; }));
    return t;
  }, [activeFiltered, intermittents]);

  const toggleAssign = async (concert, interId) => {
    const has = (concert.intermittent_ids || []).includes(interId);
    setConcerts((prev) =>
      prev.map((c) =>
        c.id === concert.id
          ? { ...c, intermittent_ids: has ? c.intermittent_ids.filter((x) => x !== interId) : [...(c.intermittent_ids || []), interId] }
          : c
      )
    );
    try {
      await api.patch(`/concerts/${concert.id}/assign`, { intermittent_id: interId });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      loadConcerts();
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/concerts/${toDelete.id}`);
      toast.success("Concert supprimé");
      setToDelete(null);
      loadConcerts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doCancel = async () => {
    try {
      await api.patch(`/concerts/${toCancel.id}/cancel`);
      toast.success("Concert annulé");
      setToCancel(null);
      loadConcerts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doRestore = async (c) => {
    try {
      await api.patch(`/concerts/${c.id}/restore`);
      toast.success("Concert réactivé");
      loadConcerts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const openPostpone = (c) => {
    setPpForm({ year: c.year, month: String(c.month), day: c.day || "", heure: c.heure || "" });
    setToPostpone(c);
  };

  const doPostpone = async () => {
    if (!ppForm.year || !ppForm.month) {
      toast.error("Renseignez l'année et le mois de report");
      return;
    }
    try {
      await api.post(`/concerts/${toPostpone.id}/postpone`, {
        year: Number(ppForm.year), month: Number(ppForm.month),
        day: ppForm.day ? Number(ppForm.day) : null,
        heure: ppForm.heure || null,
      });
      toast.success(`Reporté au ${ppForm.day ? ppForm.day + " " : ""}${MONTHS_FR[Number(ppForm.month)]} ${ppForm.year}`);
      setToPostpone(null);
      const { data: p } = await api.get("/periods");
      setPeriods(p);
      loadConcerts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const cols = ["day", "heure", "concert", "salle", "ville", "prod", "contrat", "config", "jauge", "commentaires"];
    const head = ["Jour", "Heure", "Concert", "Salle", "Ville", "Prod", "Contrat", "Config", "Jauge", "Commentaires",
      "L'autre solution", "RPO", "BB prod", "Autres Lyon", "Autre Clermont / Montluçon", "Autres St Étienne / Roanne", "Autres",
      ...intermittents.map((i) => i.name)];
    const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((c) => [
      ...cols.map((k) => q(c[k])),
      c.autre_solution ? "1" : "", c.rpo ? "1" : "", c.bb_prod ? "1" : "",
      q(c.autres_lyon), q(c.autres_clermont_montlucon), q(c.autres_st_etienne_roanne), q(c.autres),
      ...intermittents.map((i) => ((c.intermittent_ids || []).includes(i.id) ? "1" : "")),
    ]);
    const csv = [head.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `planning_${MONTHS_FR[month]}_${year}.csv`;
    a.click();
  };

  const cancelledCount = useMemo(() => filtered.filter((c) => c.status === "cancelled" && !isMontage(c)).length, [filtered]);
  const postponedCount = useMemo(() => filtered.filter((c) => c.status === "postponed" && !isMontage(c)).length, [filtered]);
  const montageCount = useMemo(() => filtered.filter((c) => isMontage(c)).length, [filtered]);

  const formatDate = (c) => {
    if (!c.day) return "—";
    return new Date(c.year, c.month - 1, c.day)
      .toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  };

  const totalJauge = activeFiltered.reduce((s, c) => {
    const n = parseInt(String(c.jauge || "").replace(/\D/g, ""), 10);
    return s + (isNaN(n) ? 0 : n);
  }, 0);

  const flagTotals = useMemo(() => ({
    autre_solution: activeFiltered.filter((c) => c.autre_solution).length,
    rpo: activeFiltered.filter((c) => c.rpo).length,
    bb_prod: activeFiltered.filter((c) => c.bb_prod).length,
  }), [activeFiltered]);

  return (
    <div className="space-y-5">
      {/* Top selectors */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
              Planning des concerts
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              <span className="font-medium text-foreground/80">Chiffres mensuels :</span> {activeFiltered.length} concert(s) / spectacle(s) · jauge cumulée à la MEV : {totalJauge.toLocaleString("fr-FR")}
              {cancelledCount > 0 && (
                <span className="text-red-400" data-testid="cancelled-count"> · {cancelledCount} concert(s) / spectacle(s) annulé(s)</span>
              )}
              {postponedCount > 0 && (
                <span className="text-amber-400" data-testid="postponed-count"> · {postponedCount} reporté(s)</span>
              )}
              {montageCount > 0 && (
                <span className="text-sky-400" data-testid="montage-count"> · {montageCount} montage(s) / démontage(s)</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={String(year ?? "")} onValueChange={(v) => {
              const y = Number(v);
              setYear(y);
              const ms = periods.months_by_year[String(y)] || [];
              setMonth(ms.includes(month) ? month : ms[0] ?? 1);
            }}>
              <SelectTrigger className="w-[120px]" data-testid="selector-year">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                {periods.years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              data-testid="btn-add-concert">
              <Plus className="h-4 w-4 mr-1.5" /> Concert
            </Button>
          </div>
        </div>

        {/* Month pills */}
        <div className="flex flex-wrap gap-2" data-testid="selector-month">
          {monthsForYear.map((m) => (
            <button
              key={m}
              onClick={() => setMonth(m)}
              data-testid={`month-pill-${m}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                month === m
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              {MONTHS_FR[m]}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher concert, salle, ville..."
              className="pl-9"
              data-testid="planning-search"
            />
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="btn-export-planning">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-240px)]">
          <table className="w-full text-sm border-collapse" data-testid="planning-spreadsheet-table">
            <thead>
              <tr className="text-muted-foreground">
                <th className="sticky-col bg-secondary px-3 py-2 text-left font-semibold w-[180px]" style={{ top: 0, zIndex: 30 }}>Date</th>
                <th className="sticky bg-secondary px-3 py-2 text-left font-semibold w-16" style={{ top: 0, left: 180, zIndex: 30 }}>Heure</th>
                <th className="sticky bg-secondary px-3 py-2 text-left font-semibold w-[200px]" style={{ top: 0, left: 244, zIndex: 30 }}>Concert</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold">Salle</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold">Ville</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold">Prod</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold">Contrat</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold">Config</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-right font-semibold">Jauge</th>
                {intermittents.map((i) => (
                  <th key={i.id} className="sticky top-0 z-20 bg-secondary px-1 py-2 font-semibold border-l border-border" title={i.name}>
                    <div className="flex flex-col items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ background: i.color }} />
                      <span className="text-[10px] whitespace-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", maxHeight: 70 }}>
                        {i.name}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="sticky top-0 z-20 bg-secondary px-2 py-2 text-center font-semibold border-l border-border text-[11px] leading-tight">L'autre<br/>solution</th>
                <th className="sticky top-0 z-20 bg-secondary px-2 py-2 text-center font-semibold border-l border-border text-[11px]">RPO</th>
                <th className="sticky top-0 z-20 bg-secondary px-2 py-2 text-center font-semibold border-l border-border text-[11px]">BB prod</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold border-l border-border min-w-[160px]">Commentaires</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold border-l border-border min-w-[150px]">Autres Lyon</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold border-l border-border min-w-[150px] text-[11px] leading-tight">Autre Clermont /<br/>Montluçon</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold border-l border-border min-w-[150px] text-[11px] leading-tight">Autres St Étienne /<br/>Roanne</th>
                <th className="sticky top-0 z-20 bg-secondary px-3 py-2 text-left font-semibold border-l border-border min-w-[130px]">Autres</th>
                <th className="sticky right-0 top-0 bg-secondary px-3 py-2 text-right font-semibold border-l border-border" style={{ zIndex: 30 }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={99} className="px-4 py-16 text-center text-muted-foreground">Chargement...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={99} className="px-4 py-16 text-center text-muted-foreground">
                  <Music4 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  Aucun concert pour {MONTHS_FR[month]} {year}
                </td></tr>
              ) : filtered.map((c) => (
                <tr key={c.id} className={`hover:bg-secondary/40 transition-colors group ${isStruck(c) ? "opacity-60 line-through" : ""}`} data-testid={`concert-row-${c.id}`}>
                  <td className="sticky-col bg-background group-hover:bg-secondary/40 px-3 py-2 font-semibold text-primary whitespace-nowrap capitalize w-[180px]" style={{ zIndex: 20 }}>{formatDate(c)}</td>
                  <td className="sticky bg-background group-hover:bg-secondary/40 px-3 py-2 whitespace-nowrap text-muted-foreground w-16" style={{ left: 180, zIndex: 20 }}>{c.heure}</td>
                  <td className="sticky bg-background group-hover:bg-secondary/40 px-3 py-2 font-medium w-[200px]" style={{ left: 244, zIndex: 20 }}>
                    <button
                      onClick={() => navigate(`/concerts/${c.id}`)}
                      data-testid={`open-concert-${c.id}`}
                      className="text-left hover:text-primary hover:underline underline-offset-2 transition-colors"
                    >
                      {c.concert}
                    </button>
                    {isStruck(c) && (
                      <span
                        data-testid={`concert-status-${c.id}`}
                        className={`ml-2 align-middle rounded px-1.5 py-0.5 text-[10px] font-semibold [text-decoration:none] inline-block ${
                          c.status === "postponed"
                            ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                            : "bg-red-500/15 text-red-400 border border-red-500/30"
                        }`}
                      >
                        {c.status === "postponed" ? "REPORTÉ" : "ANNULÉ"}
                      </span>
                    )}
                    {isMontage(c) && (
                      <span
                        data-testid={`concert-montage-${c.id}`}
                        className="ml-2 align-middle rounded px-1.5 py-0.5 text-[10px] font-semibold [text-decoration:none] inline-block bg-sky-500/15 text-sky-400 border border-sky-500/30"
                      >
                        MONTAGE / DÉMONTAGE
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{c.salle}</td>
                  <td className="px-3 py-2">{c.ville}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{c.prod}</td>
                  <td className="px-3 py-2">
                    {c.contrat && <span className="rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 px-1.5 py-0.5 text-xs">{c.contrat}</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">{c.config}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.jauge}</td>
                  {intermittents.map((i) => {
                    const checked = (c.intermittent_ids || []).includes(i.id);
                    return (
                      <td key={i.id} className="px-1 py-2 text-center border-l border-border/50">
                        <button
                          onClick={() => toggleAssign(c, i.id)}
                          data-testid={`intermittent-checkbox-${i.id}-${c.id}`}
                          className={`h-5 w-5 rounded border transition-colors ${
                            checked ? "border-transparent" : "border-border hover:border-primary/60"
                          }`}
                          style={checked ? { background: i.color } : {}}
                          title={`${i.name} — ${c.concert}`}
                        >
                          {checked && <span className="text-black text-xs font-bold">✓</span>}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center border-l border-border/50">{c.autre_solution ? <span className="text-emerald-400 font-bold">✓</span> : ""}</td>
                  <td className="px-2 py-2 text-center border-l border-border/50">{c.rpo ? <span className="text-emerald-400 font-bold">✓</span> : ""}</td>
                  <td className="px-2 py-2 text-center border-l border-border/50">{c.bb_prod ? <span className="text-emerald-400 font-bold">✓</span> : ""}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs border-l border-border/50 max-w-[240px] whitespace-pre-wrap">{c.commentaires}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs border-l border-border/50 whitespace-pre-wrap">{c.autres_lyon}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs border-l border-border/50 whitespace-pre-wrap">{c.autres_clermont_montlucon}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs border-l border-border/50 whitespace-pre-wrap">{c.autres_st_etienne_roanne}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs border-l border-border/50 whitespace-pre-wrap">{c.autres}</td>
                  <td className="px-3 py-2 text-right sticky right-0 bg-background group-hover:bg-secondary/40 border-l border-border/50 whitespace-nowrap [text-decoration:none]">
                    <div className="inline-flex items-center gap-0.5">
                      {!isStruck(c) ? (
                        <>
                          <button onClick={() => openPostpone(c)}
                            className="text-muted-foreground hover:text-amber-400 p-1" title="Reporter" data-testid={`postpone-concert-${c.id}`}>
                            <CalendarClock className="h-4 w-4" />
                          </button>
                          <button onClick={() => setToCancel(c)}
                            className="text-muted-foreground hover:text-red-400 p-1" title="Annuler" data-testid={`cancel-concert-${c.id}`}>
                            <Ban className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => doRestore(c)}
                          className="text-muted-foreground hover:text-emerald-400 p-1" title="Réactiver" data-testid={`restore-concert-${c.id}`}>
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => { setEditing(c); setModalOpen(true); }}
                        className="text-muted-foreground hover:text-primary p-1" data-testid={`edit-concert-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setToDelete(c)}
                        className="text-muted-foreground hover:text-destructive p-1" data-testid={`delete-concert-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {!loading && filtered.length > 0 && (
              <tfoot>
                <tr className="bg-secondary/60 font-semibold">
                  <td className="sticky-col bg-secondary/95 px-3 py-2 text-primary">Σ</td>
                  <td colSpan={7} className="px-3 py-2 text-muted-foreground text-xs">Total affectations</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totalJauge.toLocaleString("fr-FR")}</td>
                  {intermittents.map((i) => (
                    <td key={i.id} className="px-1 py-2 text-center tabular-nums border-l border-border" data-testid={`inter-total-${i.id}`}>
                      {interTotals[i.id] || ""}
                    </td>
                  ))}
                  <td className="border-l border-border/50 px-2 py-2 text-center tabular-nums" data-testid="total-autre-solution">{flagTotals.autre_solution || ""}</td>
                  <td className="border-l border-border/50 px-2 py-2 text-center tabular-nums" data-testid="total-rpo">{flagTotals.rpo || ""}</td>
                  <td className="border-l border-border/50 px-2 py-2 text-center tabular-nums" data-testid="total-bb-prod">{flagTotals.bb_prod || ""}</td>
                  <td className="border-l border-border/50" />
                  <td className="border-l border-border/50" />
                  <td className="border-l border-border/50" />
                  <td className="border-l border-border/50" />
                  <td className="border-l border-border/50" />
                  <td className="sticky right-0 bg-secondary/95 border-l border-border/50" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <ConcertModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        concert={editing}
        year={year}
        month={month}
        intermittents={intermittents}
        onSaved={loadConcerts}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-concert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce concert ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {toDelete?.concert} » sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-concert">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(toCancel)} onOpenChange={(o) => !o && setToCancel(null)}>
        <AlertDialogContent data-testid="cancel-concert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler ce concert ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {toCancel?.concert} » restera affiché mais barré, et ne sera plus compté dans les statistiques.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Retour</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-cancel-concert">
              Annuler le concert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(toPostpone)} onOpenChange={(o) => !o && setToPostpone(null)}>
        <DialogContent data-testid="postpone-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reporter le concert</DialogTitle>
            <DialogDescription>
              « {toPostpone?.concert} » sera marqué REPORTÉ (barré, hors statistiques) et un nouveau concert sera créé à la nouvelle date.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Année</Label>
              <Input type="number" value={ppForm.year} onChange={(e) => setPpForm((f) => ({ ...f, year: e.target.value }))} data-testid="postpone-year" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mois</Label>
              <Select value={String(ppForm.month)} onValueChange={(v) => setPpForm((f) => ({ ...f, month: v }))}>
                <SelectTrigger data-testid="postpone-month"><SelectValue placeholder="Mois" /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <SelectItem key={m} value={String(m)}>{MONTHS_FR[m]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Jour</Label>
              <Input type="number" min="1" max="31" value={ppForm.day} onChange={(e) => setPpForm((f) => ({ ...f, day: e.target.value }))} data-testid="postpone-day" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Heure</Label>
              <Input value={ppForm.heure} onChange={(e) => setPpForm((f) => ({ ...f, heure: e.target.value }))} placeholder="20h00" data-testid="postpone-heure" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setToPostpone(null)}>Annuler</Button>
            <Button onClick={doPostpone} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="confirm-postpone">
              Confirmer le report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
